import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { useMomentOnce } from "@/app/use-moment";
import { useDraft } from "../draft-store";
import { hasDate, hasTime, setAccuracy, setDate, setMinutes, setPlace, setQuery, toBirthData } from "../draft";
import { PATHS } from "../destinations";
import { DateStep } from "../screens/DateStep";
import { PlaceStep } from "../screens/PlaceStep";
import { TimeStep } from "../screens/TimeStep";
import { useOnboardingShared } from "../use-onboarding-shared";
import { PLACE_QUERY_MIN, usePlaceSearch, type PlaceSearch } from "../use-place-search";
import { useAsyncAction } from "../use-async-action";
import { Working } from "@/ui/Working";

/** Step 1 of 3. */
export function DateRoute() {
  const { draft: store, analytics } = useServices();
  useMomentOnce({ name: "onboarding started" });
  const draft = useDraft(store);
  const navigate = useNavigate();
  return (
    <DateStep
      date={draft.date}
      onChange={(date) => store.update((d) => setDate(d, date))}
      onContinue={() => {
        analytics.track({ name: "birth date entered" });
        navigate(PATHS.time);
      }}
    />
  );
}

/** Step 2 of 3. Sends the person back to step 1 if it was skipped by URL. */
export function TimeRoute() {
  const { draft: store, analytics } = useServices();
  const draft = useDraft(store);
  const navigate = useNavigate();
  useEffect(() => {
    if (draft.date === null) navigate(PATHS.date, { replace: true });
  }, [draft.date, navigate]);
  return (
    <TimeStep
      minutes={draft.minutes}
      accuracy={draft.accuracy}
      onChangeMinutes={(minutes) => store.update((d) => setMinutes(d, minutes))}
      onChangeAccuracy={(accuracy) => store.update((d) => setAccuracy(d, accuracy))}
      onContinue={() => {
        // Only how sure they are of the time, never the time itself.
        analytics.track({ name: "birth time entered", accuracy: draft.accuracy });
        navigate(PATHS.place);
      }}
    />
  );
}

/**
 * Step 3 of 3, and the compute. The week comes from the unauthenticated
 * week-preview endpoint so the reveal can happen before there is an account;
 * nothing is persisted server-side until the account exists.
 */
export function PlaceRoute() {
  const { api, draft: store, analytics } = useServices();
  const draft = useDraft(store);
  const navigate = useNavigate();
  const shared = useOnboardingShared();
  const search = usePlaceSearch(api, draft.query);
  const compute = useAsyncAction("We couldn’t read your week just now. Please try again.");
  usePlaceSearchMoments(draft.query, search);

  useEffect(() => {
    if (!hasDate(draft)) navigate(PATHS.date, { replace: true });
    else if (!hasTime(draft)) navigate(PATHS.time, { replace: true });
  }, [draft, navigate]);

  async function showChart() {
    const birth = toBirthData(draft);
    if (!birth) return;
    const ok = await compute.run(async () => {
      shared.setWeek(await api.previewWeek(birth));
    });
    if (!ok) {
      analytics.track({ name: "week preview failed" });
      return;
    }
    analytics.track({ name: "birth details completed" });
    navigate(PATHS.reveal);
  }

  if (compute.busy) return <Working message="Reading your week…" />;

  return (
    <PlaceStep
      query={draft.query}
      results={search.results}
      searching={search.searching}
      place={draft.place}
      error={compute.error ?? search.error}
      onQueryChange={(query) => store.update((d) => setQuery(d, query))}
      onSelect={(place) => {
        analytics.track({ name: "place chosen" });
        store.update((d) => setPlace(d, place));
      }}
      onContinue={showChart}
      busy={compute.busy}
    />
  );
}

/**
 * The place search, as moments: that they searched at all, and whether a
 * search came back empty or failed. Once per visit to the step each, so a
 * person typing a name letter by letter is one search, not twelve. The
 * query itself is never recorded.
 */
function usePlaceSearchMoments(query: string, search: PlaceSearch): void {
  useMomentOnce(query.trim().length >= PLACE_QUERY_MIN ? { name: "place searched" } : null);
  useMomentOnce(
    search.answered && search.error
      ? { name: "place search failed", reason: "error" }
      : search.answered && search.results.length === 0
        ? { name: "place search failed", reason: "no_results" }
        : null,
  );
}
